import { useState } from 'react';
import { api, throwIfNotOk } from '../../lib/api';

/** Fixed mask: deliberately reveals nothing (no length, no trailing characters). */
export const SERIAL_MASK = '••••••••';

/**
 * SIL-1269: the asset serial is sensitive, so it is never in a page-load
 * payload. This renders a fixed mask plus a "Reveal" link; a click calls the
 * audited GET /api/client/assets/:assetId/serial. The value lives in component
 * state only (no query cache, no storage, never in a URL) and disappears when
 * the component unmounts, e.g. on navigation.
 */
export function MaskedSerial({
  assetId,
  hasSerial,
  className,
  emptyLabel = '—',
}: {
  assetId: string;
  hasSerial: boolean;
  className?: string;
  emptyLabel?: string;
}) {
  const [serial, setSerial] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!hasSerial) return <span className={className}>{emptyLabel}</span>;

  async function reveal() {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.api.client.assets[':assetId'].serial.$get({ param: { assetId } });
      await throwIfNotOk(res);
      const body = (await res.json()) as unknown as { serialNumber: string };
      setSerial(body.serialNumber);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }

  if (serial !== null) {
    return (
      <span className={className} data-testid="serial-revealed">
        {serial}{' '}
        <button
          type="button"
          className="text-xs font-medium text-blue-600 hover:text-blue-800"
          onClick={() => setSerial(null)}
        >
          Hide
        </button>
      </span>
    );
  }

  return (
    <span className={className} data-testid="serial-masked">
      <span aria-label="Serial number hidden">{SERIAL_MASK}</span>{' '}
      <button
        type="button"
        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
        onClick={() => void reveal()}
        disabled={loading}
      >
        {loading ? 'Revealing...' : 'Reveal'}
      </button>
      {failed && (
        <span role="alert" className="ml-2 text-xs text-red-600">
          Could not reveal the serial.
        </span>
      )}
    </span>
  );
}
